import { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { usePages } from '@erpsquad/common/runtime';
import { useLocation } from 'react-router-dom';
interface FilterParams {
	skip: number;
	limit: number;
	search?: string;
	select?: string;
	order?: string;
	filters?: string;
}

interface ActivePage {
	sort?: { id: string; desc: boolean };
	filterQueryString?: string;
	search?: string;
	visible_columns?: { visible: boolean; accessorKey: string }[];
}

const useGenericDataFetcher = (
	cols?: ActivePage,
	pagination: { skip: number; limit: number },
	fetchAction: (filters: FilterParams) => void,
	customeFilter: any = '',
	customDeps?: any[],
	showCreatedBy: boolean = true,
	customFields?: any = []
) => {
	const { activePage } = usePages();
	const dispatch = useDispatch();
	const location = useLocation();

	const sourceColumns = Array.isArray(cols) ? cols?.filter((col: any) => col.visible) : activePage?.visible_columns;

	const [trackedPath, setTrackedPath] = useState<string | null>(null);
	const [trackedColumns, setTrackedColumns] = useState<any[]>([]);

	useEffect(() => {
		if (trackedPath !== location?.pathname) {
			setTrackedPath(location?.pathname);
			setTrackedColumns([]);
		}
	}, [location?.pathname, trackedPath]);

	useEffect(() => {
		if (trackedPath === location?.pathname && sourceColumns?.length > 0) {
			if (JSON.stringify(trackedColumns) !== JSON.stringify(sourceColumns)) {
				setTrackedColumns(sourceColumns);
			}
		}
	}, [location?.pathname, trackedPath, sourceColumns]);

	const getProcessedFilters = useCallback(() => {
		const { filterQueryString } = activePage || {};
		const queryString = filterQueryString?.replaceAll(/[()]/g, "") || '';
		const extraQueryString = queryString.length ? `&${queryString}` : '';
		let filters = customeFilter;

		if (extraQueryString) {
			filters = filters ? `(${filters}${extraQueryString})` : `(${extraQueryString})`;
		} else if (filters) {
			filters = `(${filters})`;
		}

		return filters || undefined;
		//eslint-disable-next-line
	}, [activePage.filterQueryString, customeFilter]);

	const fetchData = useCallback(() => {
		if (!fetchAction) {
			return;
		}
		const { sort = null, search } = activePage;

		if (!trackedColumns || trackedColumns.length === 0) return;

		const filters = getProcessedFilters();
		const visibleColumns = trackedColumns?.filter((vc) => vc.visible)

		const selectPayload = visibleColumns?.length > 0 ? `[${visibleColumns
			?.map((item) =>
				item.accessorKey?.includes(".")
					? item.accessorKey.split(".")[0]
					: item.accessorKey
			)}${showCreatedBy ? ',created_at,created_by_data' : ''}${customFields?.length ? `,${customFields?.join(',')}` : ''}]` : undefined
		const payload: FilterParams = {
			skip: activePage?.skip || pagination?.skip,
			limit: pagination.limit,
			search,
			select: selectPayload,
			...(sort && { order: `${sort.id}:${sort.desc ? -1 : 1}` }),
			...(filters && { filters }),
		};

		dispatch(fetchAction(payload));
		//eslint-disable-next-line
	}, [
		trackedColumns,
		activePage.sort,
		activePage.filterQueryString,
		activePage.search,
		// pagination?.skip intentionally omitted: the Pagination component already
		// mirrors skip into activePage.skip on pageNo change; including pagination.skip
		// here would make fetchData identity change twice per pagination click,
		// triggering the listing useEffect twice and causing duplicate API calls.
		activePage?.skip,
		pagination?.limit,
		dispatch,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...(customDeps || []),
	]);



	return fetchData;
};

export default useGenericDataFetcher;
